import React, { useEffect, useState } from "react";
import styles from "./CustomColumnFormFields.module.css";
import { Field, useForm, useFormState } from "react-final-form";
import { requiredWithMessage } from "common/validate";
import { FormFieldLabel } from "features/Common/modules/Form/modules/FormFieldLabel/FormFieldLabel";
import {
  COLUMN_FIELD_TYPE_OPTIONS,
  CUSTOM_COLUMN_FORM_KEYS,
  CUSTOMER_TABLE_OPTIONS,
  DETAILS_ACC_ID,
  LEAD_TABLE_OPTIONS,
  OLDER_CONTACT_VALUE_OPTIONS,
  SETTINGS_ACC_ID,
  TABLE_OPTIONS,
} from "./constants/CustomColumnFormField.constants";
import { ExlyDropdown, ExlyInput } from "common/form";
import ExlyRadioGroup from "common/form/ExlyRadioGroup/Index";
import ExlyCheckboxGroup from "common/form/ExlyCheckboxGroup";
import {
  InputFieldTypeValues,
  VALIDATORS,
} from "./utils/CustomColumnFormFields.utils";
import { CustomColumnAccordian } from "../CustomColumnAccordian/CustomColumnAccordian";
import { DefaultValueField } from "./modules/DefaultValueField/DefaultValueField";
import { getRAUrlPathName } from "utils/urlUtils";
import { app_route_ids, app_route_keys } from "constants/urlPaths";

export const CustomColumnFormFields = ({ isEdit = false }) => {
  const [expandedAccordions, setExpandedAccordionIndex] = useState({
    [DETAILS_ACC_ID]: true,
    [SETTINGS_ACC_ID]: isEdit,
  });

  const isLeadsTable =
    getRAUrlPathName() === app_route_ids[app_route_keys.leads];

  const {
    values: {
      [CUSTOM_COLUMN_FORM_KEYS.column_name.key]: columnName,
      [CUSTOM_COLUMN_FORM_KEYS.field_type.key]: fieldType,
      [CUSTOM_COLUMN_FORM_KEYS.options.key]: options,
      [CUSTOM_COLUMN_FORM_KEYS.default_value.key]: defaultValue,
      [CUSTOM_COLUMN_FORM_KEYS.custom_value.key]: customValue,
      [CUSTOM_COLUMN_FORM_KEYS.older_contacts.key]: olderContactsSelection,
      [CUSTOM_COLUMN_FORM_KEYS.sections.key]: sections,
    },
  } = useFormState();

  const form = useForm();

  const handleType = ({ value }) => {
    form.change(CUSTOM_COLUMN_FORM_KEYS.field_type.key, value);
    if (
      [
        COLUMN_FIELD_TYPE_OPTIONS.single_select.value,
        COLUMN_FIELD_TYPE_OPTIONS.multiple_select.value,
      ].includes(value)
    ) {
      form.change(CUSTOM_COLUMN_FORM_KEYS.options.key, [undefined, undefined]);
    }
    form.change(CUSTOM_COLUMN_FORM_KEYS.default_value.key, undefined);
    form.change(CUSTOM_COLUMN_FORM_KEYS.custom_value.key, undefined);
  };

  useEffect(() => {
    if (COLUMN_FIELD_TYPE_OPTIONS.multiple_select.value === fieldType) {
      const defaultFieldValue = defaultValue?.filter((item) =>
        options.some((obj) => obj.option === item)
      );
      const customFieldValue = customValue?.filter((item) =>
        options.some((obj) => obj.option === item)
      );
      form.change(CUSTOM_COLUMN_FORM_KEYS.custom_value.key, customFieldValue);
      form.change(CUSTOM_COLUMN_FORM_KEYS.default_value.key, defaultFieldValue);
    }
  }, [options]);

  useEffect(() => {
    // If default value present, set older contacts selection to default
    if (defaultValue?.length > 0 && !olderContactsSelection) {
      form.change(
        CUSTOM_COLUMN_FORM_KEYS.older_contacts.key,
        OLDER_CONTACT_VALUE_OPTIONS.DEFAULT.value
      );
    }
  }, [defaultValue]);

  useEffect(() => {
    form.change(CUSTOM_COLUMN_FORM_KEYS.sections.key, [
      isLeadsTable ? TABLE_OPTIONS.LEADS.value : TABLE_OPTIONS.CUSTOMERS.value,
    ]);
  }, []);

  const handleExpand = (id) => {
    setExpandedAccordionIndex((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const isColumnDetailsChecked =
    columnName?.length > 2 && fieldType !== undefined;

  const isColumnSettingsChecked = isEdit
    ? true
    : olderContactsSelection !== undefined &&
      (olderContactsSelection === OLDER_CONTACT_VALUE_OPTIONS.CUSTOM.value
        ? customValue?.length > 0
        : defaultValue?.length > 0);

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-bg-[#eeecf9] tw-gap-[20px] tw-py-[20px] tw-px-[24px] tw-min-h-[500px]">
      <CustomColumnAccordian
        label="Columnn Details"
        open={expandedAccordions[DETAILS_ACC_ID]}
        onExpand={() => handleExpand(DETAILS_ACC_ID)}
        checked={isColumnDetailsChecked}
      >
        <div className="tw-p-[16px] tw-flex tw-flex-col tw-gap-[16px] tw-w-full">
          <Field
            name={CUSTOM_COLUMN_FORM_KEYS.column_name.key}
            label={
              <FormFieldLabel
                formKeys={CUSTOM_COLUMN_FORM_KEYS}
                formKey={CUSTOM_COLUMN_FORM_KEYS.column_name.key}
                required
                classes={{ spanClassName: "ml-0" }}
              />
            }
            validate={requiredWithMessage("Please enter column name.")}
            size="small"
            fullWidth
            component={ExlyInput}
            placeholder={CUSTOM_COLUMN_FORM_KEYS.column_name.placeholder}
          />
          <Field
            name={CUSTOM_COLUMN_FORM_KEYS.field_type.key}
            label={
              <FormFieldLabel
                formKeys={CUSTOM_COLUMN_FORM_KEYS}
                formKey={CUSTOM_COLUMN_FORM_KEYS.field_type.key}
                required
                classes={{ spanClassName: "ml-0" }}
              />
            }
            validate={requiredWithMessage("Please enter field type.")}
            size="small"
            component={ExlyDropdown}
            onChange={(value) => handleType({ value })}
            fullWidth
            placeholder={CUSTOM_COLUMN_FORM_KEYS.field_type.placeholder}
            options={Object.values(COLUMN_FIELD_TYPE_OPTIONS)}
            disabled={isEdit}
          />
          {InputFieldTypeValues({ type: fieldType })}
        </div>
      </CustomColumnAccordian>
      <CustomColumnAccordian
        label="Column Settings"
        open={expandedAccordions[SETTINGS_ACC_ID]}
        onExpand={() => handleExpand(SETTINGS_ACC_ID)}
        checked={isColumnSettingsChecked}
      >
        <div className="tw-p-[16px] tw-flex tw-flex-col tw-gap-[16px] tw-w-full">
          <DefaultValueField
            formKeyObj={CUSTOM_COLUMN_FORM_KEYS.default_value}
            validate={VALIDATORS[CUSTOM_COLUMN_FORM_KEYS.default_value.key]({
              fieldType,
            })}
          />
          {!isEdit && (
            <>
              <Field
                name={CUSTOM_COLUMN_FORM_KEYS.older_contacts.key}
                component={ExlyRadioGroup}
                options={Object.values(OLDER_CONTACT_VALUE_OPTIONS)}
                formControlLabelClassName="tw-mb-[6px]"
                label={
                  <FormFieldLabel
                    formKeys={CUSTOM_COLUMN_FORM_KEYS}
                    formKey={CUSTOM_COLUMN_FORM_KEYS.older_contacts.key}
                    required
                    classes={{ spanClassName: "ml-0" }}
                  />
                }
              />
              {olderContactsSelection ===
                OLDER_CONTACT_VALUE_OPTIONS.CUSTOM.value && (
                <DefaultValueField
                  formKeyObj={CUSTOM_COLUMN_FORM_KEYS.custom_value}
                  required
                  validate={VALIDATORS[
                    CUSTOM_COLUMN_FORM_KEYS.custom_value.key
                  ]({ fieldType })}
                />
              )}
              <div>
                <div className="mt-0 mb-2">
                  <FormFieldLabel
                    formKeys={CUSTOM_COLUMN_FORM_KEYS}
                    formKey={CUSTOM_COLUMN_FORM_KEYS.sections.key}
                    classes={{ spanClassName: "ml-0" }}
                  />
                </div>
                <Field
                  name={CUSTOM_COLUMN_FORM_KEYS.sections.key}
                  component={ExlyCheckboxGroup}
                  options={Object.values(
                    isLeadsTable ? LEAD_TABLE_OPTIONS : CUSTOMER_TABLE_OPTIONS
                  )}
                  value={sections}
                  classes={{ label: styles.checboxLabelClassName }}
                />
              </div>
            </>
          )}
        </div>
      </CustomColumnAccordian>
    </div>
  );
};
